import { useCallback, useEffect, useRef, useState } from 'react';
import toast from 'react-hot-toast';
import {
  LTI_CONTENT_LABEL,
  loadLtiContent,
  type LtiContentItem,
} from '@/services/lti-content.catalogue';
import { LtiResourceType } from '@/types/lti.types';

/** What the catalogue hook hands back to a picker. */
export interface LtiContentOptions {
  optionsFor: (type: LtiResourceType) => LtiContentItem[] | undefined;
  isLoading: (type: LtiResourceType) => boolean;
  load: (type: LtiResourceType) => Promise<void>;
}

/** The content catalogue behind a "what should this link open?" dropdown. */
export function useLtiContentOptions(initialType?: LtiResourceType): LtiContentOptions {
  const [cache, setCache] = useState<Partial<Record<LtiResourceType, LtiContentItem[]>>>({});
  const [loadingType, setLoadingType] = useState<LtiResourceType | null>(null);
  /** Types already fetched or in flight, so a re-render cannot fetch twice. */
  const requested = useRef(new Set<LtiResourceType>());

  const load = useCallback(async (type: LtiResourceType) => {
    if (requested.current.has(type)) return;
    requested.current.add(type);

    setLoadingType(type);
    try {
      const items = await loadLtiContent(type);
      setCache(prev => ({ ...prev, [type]: items }));
    } catch (error) {
      requested.current.delete(type); // let the next open retry
      console.error(`Failed to load ${LTI_CONTENT_LABEL[type]}s for LTI mapping`, error);
      toast.error(`Could not load your ${LTI_CONTENT_LABEL[type]}s.`);
    } finally {
      setLoadingType(null);
    }
  }, []);

  useEffect(() => {
    if (initialType !== undefined) void load(initialType);
  }, [initialType, load]);

  return {
    optionsFor: (type: LtiResourceType) => cache[type],
    isLoading: (type: LtiResourceType) => loadingType === type && !cache[type],
    load,
  };
}
